import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, AlertCircle, Building2, Tag, MessageCircle, Calendar, User, Mail, Phone, CheckCircle2, Image as ImageIcon } from 'lucide-react';
import ImageViewerModal from './ImageViewerModal';

const TicketDetailModal = ({ isOpen, onClose, ticket }) => {
  const [currentStatus, setCurrentStatus] = useState(ticket?.status || '');
  const [isStatusChanged, setIsStatusChanged] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);

  useEffect(() => {
    if (ticket) {
      setCurrentStatus(ticket.status);
      setIsStatusChanged(false);
    }
  }, [ticket]);

  if (!isOpen) return null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'resolved':
        return 'bg-emerald-500';
      case 'pending':
        return 'bg-amber-500';
      case 'in progress':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleStatusChange = (newStatus) => {
    setCurrentStatus(newStatus);
    setIsStatusChanged(true);
  };

  const handleSave = () => {
    // Here you would typically make an API call to update the ticket
    console.log('Saving new status:', currentStatus);
    setIsStatusChanged(false);
  };

  // Dummy images array - replace with actual ticket images
  const dummyImages = [
    'https://picsum.photos/800/600?random=1',
    'https://picsum.photos/800/600?random=2',
    'https://picsum.photos/800/600?random=3',
    'https://picsum.photos/800/600?random=4'
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div 
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-white rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-[#FF6C6C] text-white p-8"
            >
              <div className="flex justify-between items-start mb-8">
                <div className="flex items-center gap-6">
                  <div className="p-3 bg-white/10 backdrop-blur-sm rounded-2xl">
                    <img 
                      src={ticket.logo || '/melcom-logo.png'} 
                      alt="Company Logo" 
                      className="w-20 h-20 object-contain"
                    />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-3xl font-bold tracking-tight">{ticket.submittedBy}</h2>
                    <div className="flex items-center gap-2 text-white/90">
                      <Building2 size={18} />
                      <span>{ticket.clinicName}</span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-full transition-all duration-200"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl">
                  <Tag size={16} className="text-white/70" />
                  <span>Ticket #{ticket.id}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl">
                  <MessageCircle size={16} className="text-white/70" />
                  <span>{ticket.issueType}</span>
                </div>
                <div className={`flex items-center gap-2 ${getStatusColor(currentStatus)} px-4 py-2 rounded-xl font-medium`}>
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  {currentStatus}
                </div>
                {dummyImages.length > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => { e.stopPropagation(); setIsImageViewerOpen(true); }}
                    className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl hover:bg-white/20 transition-all"
                  >
                    <ImageIcon size={16} className="text-white/70" />
                    <span>View Images ({dummyImages.length})</span>
                  </motion.button>
                )}
              </div>
            </motion.div>

            {/* Content */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-3 gap-8 p-8"
            >
              {/* Left Column - Contact Info */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-4"
              >
                <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                <div className="space-y-3">
                  <div className="p-4 bg-gray-50 rounded-xl space-y-2 border border-gray-100">
                    <div className="flex items-center gap-2 text-gray-500">
                      <User size={16} />
                      <p className="text-sm font-medium">Contact Person</p>
                    </div>
                    <p className="font-semibold text-gray-900">{ticket.submittedBy}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl space-y-2 border border-gray-100">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Mail size={16} />
                      <p className="text-sm font-medium">Email Address</p>
                    </div>
                    <p className="font-semibold text-gray-900">{ticket.email || 'support@example.com'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl space-y-2 border border-gray-100">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Phone size={16} />
                      <p className="text-sm font-medium">Phone Number</p>
                    </div>
                    <p className="font-semibold text-gray-900">{ticket.phone || '+233 XX XXX XXXX'}</p>
                  </div>
                </div>
              </motion.div>

              {/* Middle Column - Issue Details */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="col-span-2 space-y-6"
              >
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900">
                    <AlertCircle size={18} className="text-[#FF6C6C]" />
                    Issue Details
                  </h3>
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-2 text-gray-500 mb-3">
                      <Calendar size={16} />
                      <span className="text-sm">Submitted on {ticket.timestamp}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      {ticket.description || "Am encountering issue with subscription"}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Update Status</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {['Resolved', 'Pending', 'In Progress'].map((status, index) => (
                      <motion.label 
                        key={status}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`
                          flex items-center gap-3 p-4 rounded-xl cursor-pointer border-2 transition-all duration-200
                          ${currentStatus.toLowerCase() === status.toLowerCase()
                            ? 'border-[#FF6C6C] bg-red-50/50 ring-2 ring-[#FF6C6C]/20' 
                            : 'border-gray-200 hover:border-[#FF6C6C]/40 hover:bg-gray-50'}
                        `}
                      >
                        <input 
                          type="radio" 
                          name="status" 
                          value={status}
                          checked={currentStatus.toLowerCase() === status.toLowerCase()}
                          onChange={() => handleStatusChange(status)}
                          className="w-4 h-4 text-[#FF6C6C] focus:ring-[#FF6C6C] border-gray-300"
                        />
                        <span className="font-medium text-gray-900">{status}</span>
                      </motion.label>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Footer */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="border-t px-8 py-6 flex justify-end gap-3 bg-gray-50"
            >
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium transition-all duration-200"
              >
                Cancel
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSave}
                disabled={!isStatusChanged}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-2
                  ${isStatusChanged 
                    ? 'bg-[#FF6C6C] text-white hover:bg-[#ff5555]' 
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'}
                `}
              >
                <CheckCircle2 size={18} />
                Save Changes
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
      <ImageViewerModal
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
        images={dummyImages}
      />
    </AnimatePresence>
  );
};

export default TicketDetailModal;
